"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const properties = [{
  id: 1,
  name: "The Executive Suite",
  location: "Central Business District",
  price: "From £120/night",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_executive.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_executive.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_executive.png-wvc-sizes",
    alt: "Modern executive suite interior with warm wood tones"
  }
}, {
  id: 2,
  name: "Contractor's Haven",
  location: "Industrial Hub",
  price: "From £95/night",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_contractor.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_contractor.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_contractor.png-wvc-sizes",
    alt: "Spacious and practical contractor accommodation"
  }
}, {
  id: 3,
  name: "Family Retreat",
  location: "Suburban Quiet Zone",
  price: "From £150/night",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_family.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_family.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/property_family.png-wvc-sizes",
    alt: "Comfortable family home living area"
  }
}];
export default function ExploreOurPrimeStays() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  return <section id="explore-our-prime-stays" className="bg-foreground text-background py-24 md:py-48 relative overflow-hidden">
      {}
      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692125/img/noise_texture.png')] mix-blend-overlay" />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        {}
        <div className="font-mono text-[0.75rem] font-semibold tracking-[0.15em] uppercase text-muted/80 mb-12">
          § 01 / PORTFOLIO
        </div>

        {}
        <h2 ref={ref} className={`font-default text-[clamp(2rem,5vw,3.5rem)] font-normal tracking-tight leading-[1.2] mb-24 transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
          <span className="block ml-8 md:ml-16 text-secondary">Explore</span>
          <span className="block">Our Prime Stays</span>
        </h2>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {properties.map((prop, i) => <div key={prop.id} data-index={i} className="transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] h-full" style={{
          transitionDelay: `${i * 100}ms`,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(30px)"
        }}>
              <Card className="rounded-md bg-background border-muted relative group transition-all duration-400 hover:-translate-y-1 shadow-sm overflow-hidden h-full flex flex-col">
                {}
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left z-20" />

                {}
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-muted">
                  {}
                  <div className="absolute top-4 left-4 w-5 h-5 border-t-[1.5px] border-l-[1.5px] border-secondary z-10" />
                  
                  <img src={prop.image.src} data-wvc-srcset={prop.image.srcset} data-wvc-sizes={prop.image.sizes} alt={prop.image.alt} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" loading="lazy" />
                </div>

                {}
                <CardContent className="flex-grow flex flex-col justify-between p-6 pt-8">
                  <h3 className="font-default text-[clamp(1.25rem,2vw,1.75rem)] font-medium tracking-normal leading-[1.3] text-foreground mb-6">
                    {prop.name}
                  </h3>
                  <div className="flex items-center justify-between mt-auto">
                    <Badge variant="secondary" className="font-mono text-[10px] bg-muted text-foreground rounded-md px-3 py-1">
                      {prop.location}
                    </Badge>
                    <span className="font-mono text-xs text-muted-foreground">
                      {prop.price}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden border-t border-muted/10 py-2 bg-foreground z-0">
        <div className="whitespace-nowrap font-mono text-[10px] text-muted/40 tracking-widest flex" style={{
        animation: "ticker-scroll 40s linear infinite",
        width: "200%"
      }}>
          <span className="w-1/2 flex justify-around">
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
          </span>
          <span className="w-1/2 flex justify-around">
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
            <span>COMFORT • RELIABILITY • CLEANLINESS</span>
          </span>
        </div>
      </div>
    </section>;
}